import React, { useEffect, useState } from "react";
import { getRingEvents, RingEvent, RingEvents } from "../services/ringService";
import RingLiveView from "./RingLiveView";

// Polling, so a highlight can be up to this late plus Ring's own delay in
// logging the event. Instant needs Ring's push notifications (see HARDWARE.md).
const REFRESH_MS = 30 * 1000;
const ACTIVE_MS = 5 * 60 * 1000;
const LOW_BATTERY = 20;

type Device = RingEvents["devices"][number];

/**
 * Where each kind of device sits on the house still, as percentages of the
 * picture. Projected from the 3D model at the still's angle (30°) with
 * window.projectPoints in tools/house-scene: the doorbell on the front door at
 * (2.01, 1.1, 0), and the camera high on the garage's front corner, just under
 * the eaves, at (-0.12, 1.95, -9.45).
 */
const SPOTS: Partial<Record<Device["kind"], { x: number; y: number }>> = {
  doorbell: { x: 52.9, y: 69.2 },
  camera: { x: 33.7, y: 45.3 },
};

/** A doorbell press or a person in the last few minutes. Plain motion (trees, cars) doesn't count. */
const recentActivity = (events: RingEvent[], deviceId: number, now: number) =>
  events.find(
    (e) =>
      e.deviceId === deviceId &&
      (e.type === "ding" || e.person) &&
      now - new Date(e.at).getTime() < ACTIVE_MS,
  );

const ago = (iso: string, now: number) => {
  const minutes = Math.floor((now - new Date(iso).getTime()) / 60000);
  return minutes < 1 ? "now" : `${minutes}m`;
};

/** Glowing touch points over the house for the doorbell and camera. */
export default function RingHotspots() {
  const [data, setData] = useState<RingEvents | null>(null);
  const [now, setNow] = useState(Date.now());
  const [live, setLive] = useState<Device | null>(null);

  useEffect(() => {
    let active = true;
    const load = () => {
      setNow(Date.now());
      getRingEvents()
        .then((result) => active && setData(result))
        .catch(() => {});
    };
    load();
    const id = window.setInterval(load, REFRESH_MS);
    return () => {
      active = false;
      window.clearInterval(id);
    };
  }, []);

  if (!data?.connected) return null;

  return (
    <>
      {data.devices.map((device) => {
        const spot = SPOTS[device.kind];
        if (!spot) return null;
        const low = device.battery !== null && device.battery <= LOW_BATTERY;
        const activity = recentActivity(data.events, device.id, now);
        return (
          <button
            key={device.id}
            type="button"
            className={`ring-spot ${low ? "is-low" : ""} ${activity ? "is-active" : ""}`}
            style={{ left: `${spot.x}%`, top: `${spot.y}%` }}
            onClick={() => setLive(device as Device)}
            title={activity?.description ?? undefined}
            aria-label={`Live view: ${device.name}`}
          >
            <span className="ring-spot__dot" aria-hidden />
            <span className="ring-spot__label">
              <strong>{device.name}</strong>
              {activity ? (
                <em>
                  {activity.type === "ding" ? "Doorbell" : "Person"} · {ago(activity.at, now)}
                </em>
              ) : (
                device.battery !== null && (
                  <span aria-label={`battery ${Math.round(device.battery)}%`}>
                    {Math.round(device.battery)}%
                  </span>
                )
              )}
            </span>
          </button>
        );
      })}
      {live && <RingLiveView device={live} onClose={() => setLive(null)} />}
    </>
  );
}
